import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Smartphone, 
  Star, 
  Clock, 
  CheckCircle, 
  Trash2,
  Phone
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';

interface Carrier {
  id: string;
  name: string;
  logo?: string;
}

interface TopupAmount {
  value: number;
  bonus?: number;
  description?: string;
}

interface TopupHistory {
  id: string;
  phoneNumber: string;
  carrierName: string;
  amount: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
}

interface FavoritePhone {
  id: string;
  phoneNumber: string;
  carrierName: string;
  nickname?: string;
  createdAt: string;
}

export default function MobileTopup() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'topup' | 'history' | 'favorites'>('topup');
  const [selectedCarrier, setSelectedCarrier] = useState<Carrier | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch carriers
  const { data: carriers, isLoading: loadingCarriers } = useQuery({
    queryKey: ['topup-carriers'],
    queryFn: async () => {
      const response = await api.get('/banking/topup/carriers');
      return response.data as Carrier[];
    },
  });

  // Fetch amounts for selected carrier
  const { data: amounts, isLoading: loadingAmounts } = useQuery({
    queryKey: ['topup-amounts', selectedCarrier?.id],
    queryFn: async () => {
      const response = await api.get(`/banking/topup/carriers/${selectedCarrier!.id}/amounts`);
      return response.data as TopupAmount[];
    },
    enabled: !!selectedCarrier,
  });

  // Fetch history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['topup-history'],
    queryFn: async () => {
      const response = await api.get('/banking/topup/history');
      return response.data as TopupHistory[];
    },
    enabled: activeTab === 'history',
  });

  // Fetch favorites
  const { data: favorites, isLoading: loadingFavorites } = useQuery({
    queryKey: ['favorite-phones'],
    queryFn: async () => {
      const response = await api.get('/banking/topup/favorites');
      return response.data as FavoritePhone[];
    },
    enabled: activeTab === 'favorites',
  });

  // Validate phone mutation
  const validateMutation = useMutation({
    mutationFn: async (phone: string) => {
      const response = await api.post('/banking/topup/validate', { phoneNumber: phone });
      return response.data;
    },
  });

  // Process topup mutation
  const topupMutation = useMutation({
    mutationFn: async (data: { phoneNumber: string; carrierId: string; amount: number }) => {
      const response = await api.post('/banking/topup', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topup-history'] });
      setShowConfirmModal(false);
      resetForm();
    },
  });

  // Save favorite mutation
  const saveFavoriteMutation = useMutation({
    mutationFn: async (data: { phoneNumber: string; carrierName: string; nickname?: string }) => {
      const response = await api.post('/banking/topup/favorites', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-phones'] });
    },
  });

  // Remove favorite mutation
  const removeFavoriteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/banking/topup/favorites/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorite-phones'] });
    },
  });

  const resetForm = () => {
    setSelectedCarrier(null);
    setSelectedAmount(null);
    setPhoneNumber('');
  };

  const handleTopup = () => {
    if (selectedCarrier && selectedAmount && phoneNumber) {
      topupMutation.mutate({
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        carrierId: selectedCarrier.id,
        amount: selectedAmount,
      });
    }
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      PENDING: 'Pendente',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const carrierColors: Record<string, string> = {
    'Vivo': 'bg-purple-500',
    'Claro': 'bg-red-500',
    'Tim': 'bg-blue-500',
    'Oi': 'bg-yellow-500',
  };

  return (
    <PageContainer
      title="Recarga de Celular"
      description="Recarregue créditos de celular"
      icon={Smartphone}
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'topup' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('topup')}
        >
          <Smartphone className="w-4 h-4 mr-2" />
          Recarregar
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('history')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
        <Button
          variant={activeTab === 'favorites' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('favorites')}
        >
          <Star className="w-4 h-4 mr-2" />
          Favoritos
        </Button>
      </div>

      {/* Topup Tab */}
      {activeTab === 'topup' && (
        <div className="space-y-6">
          {/* Step 1: Select Carrier */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-surface-100 mb-4">
                1. Selecione a operadora
              </h3>
              {loadingCarriers ? (
                <div className="text-center py-4 text-surface-400">Carregando...</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {carriers?.map((carrier) => (
                    <button
                      key={carrier.id}
                      onClick={() => {
                        setSelectedCarrier(carrier);
                        setSelectedAmount(null);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        selectedCarrier?.id === carrier.id
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-700 hover:border-surface-600'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full ${carrierColors[carrier.name] || 'bg-surface-600'} mx-auto mb-2 flex items-center justify-center`}>
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-surface-100 font-medium text-center">{carrier.name}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Step 2: Select Amount */}
          {selectedCarrier && (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  2. Selecione o valor
                </h3>
                {loadingAmounts ? (
                  <div className="text-center py-4 text-surface-400">Carregando...</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {amounts?.map((amount) => (
                      <button
                        key={amount.value}
                        onClick={() => setSelectedAmount(amount.value)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedAmount === amount.value
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <p className="text-xl font-bold text-surface-100">
                          {formatCurrency(amount.value)}
                        </p>
                        {amount.bonus && (
                          <p className="text-sm text-success mt-1">
                            +{formatCurrency(amount.bonus)} bônus
                          </p>
                        )}
                        {amount.description && (
                          <p className="text-xs text-surface-400 mt-1">{amount.description}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Step 3: Enter Phone */}
          {selectedAmount && (
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-surface-100 mb-4">
                  3. Digite o número do celular
                </h3>
                <div className="flex gap-4">
                  <Input
                    placeholder="(00) 00000-0000"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="flex-1"
                    maxLength={15}
                  />
                  <Button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={phoneNumber.replace(/\D/g, '').length < 11}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Recarregar
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Histórico de Recargas
            </h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full ${carrierColors[item.carrierName] || 'bg-surface-600'} flex items-center justify-center`}>
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">
                          {formatPhone(item.phoneNumber)}
                        </p>
                        <p className="text-sm text-surface-400">
                          {item.carrierName} • {formatDate(item.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-surface-100 font-medium">
                        {formatCurrency(item.amount)}
                      </p>
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhuma recarga encontrada
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Números Favoritos
            </h3>
            {loadingFavorites ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : favorites && favorites.length > 0 ? (
              <div className="space-y-4">
                {favorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="flex items-center justify-between p-4 bg-surface-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full ${carrierColors[fav.carrierName] || 'bg-surface-600'} flex items-center justify-center`}>
                        <Star className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-surface-100 font-medium">
                          {fav.nickname || formatPhone(fav.phoneNumber)}
                        </p>
                        <p className="text-sm text-surface-400">
                          {fav.carrierName} • {formatPhone(fav.phoneNumber)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setPhoneNumber(fav.phoneNumber);
                          setActiveTab('topup');
                        }}
                      >
                        Recarregar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFavoriteMutation.mutate(fav.id)}
                      >
                        <Trash2 className="w-4 h-4 text-error" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhum favorito salvo
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Recarga"
      >
        <div className="space-y-4">
          <div className="bg-surface-800 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-surface-400">Operadora</span>
              <span className="text-surface-100">{selectedCarrier?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-400">Número</span>
              <span className="text-surface-100">{formatPhone(phoneNumber)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-surface-400">Valor</span>
              <span className="text-primary-400 font-bold">
                {selectedAmount && formatCurrency(selectedAmount)}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="saveFavorite"
              className="rounded border-surface-600"
            />
            <label htmlFor="saveFavorite" className="text-sm text-surface-300">
              Salvar número como favorito
            </label>
          </div>

          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleTopup}
              disabled={topupMutation.isPending}
              className="flex-1"
            >
              {topupMutation.isPending ? 'Processando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
